/**
 * push-adapter.ts — 推送域真实 DTO → mock 富集行的塑形纯函数
 *
 * 接真改造：真实后端返回规范化 snake_case DTO（PushTemplateResponse / PushTaskResponse，
 * 见 @novel-admin/shared push.ts），而设计稿页面（PushPage.tsx）消费的是 mock 的 camelCase
 * 富集行（PushTemplate / PushHistoryRow，见 ~/data/mock/types.ts）。本文件做单向塑形，
 * 让页面 0 改动、设计 100% 还原。纯函数 + 严格类型，便于单测。
 *
 * 字段映射要点（DTO → mock）：
 *   task_type(enum)        → type(中文标签) + category(营销/功能)   —— 经 TASK_TYPE_META 固定表
 *   title_i18n.zh/.en      → displayTitle / originalTitle
 *   Object.keys(title_i18n)→ langs（已录入语言 = i18n map 的 key 集）
 *   status(EN 枚举)        → status(中文)                          —— STATUS_ZH 映射
 *   target_scope/app_id    → scope（'全部 App' 或具体 App 名）
 *   total_delivered        → delivered
 *   total_delivered+failed → audience（真 API 无总受众，用已送达+失败近似）
 *   (无字段)               → opened=null（真 API 无打开埋点 → 页面渲染「--」，不显假 0 / 不算开启率）
 *
 * 真 API 普遍缺 mock 的聚合/关联名字段，缺字段处理见各 adapter 内 `// 缺：` 注释。
 */

import type { PushTaskResponse, PushTaskType, PushTemplateResponse } from '@novel-admin/shared';
import { formatPushTemplateName } from '~/lib/push-template-display';
import type { PushHistoryRow, PushTemplate } from '../mock/types';

/**
 * 每个 task_type 的展示元数据：中文标签 + 类别。
 * - type：页面卡片/表格列展示的中文推送类型名（镜像 mock PUSH_TEMPLATES 既有命名）
 * - category：页面「类别」筛选只认 '营销' / '功能' 两个字面量（见 PushPage FilterButton）。
 *   划分依据 dev-docs 推送分类：营销类（签到/召回/荐书/充值促销）受营销开关影响；
 *   功能类（连载/下架/续费成功/客服）为事务性通知，不受营销开关影响。
 */
const TASK_TYPE_META: Record<PushTaskType, { type: string; category: '营销' | '功能' }> = {
  SIGNIN_REMIND: { type: '签到提醒', category: '营销' },
  USER_RECALL: { type: '用户召回', category: '营销' },
  BOOK_RECOMMEND: { type: '书籍推荐', category: '营销' },
  TOPUP_PROMO: { type: '充值活动', category: '营销' },
  CHAPTER_UPDATE: { type: '连载更新', category: '功能' },
  BOOK_DELIST_NOTICE: { type: '书籍下架', category: '功能' },
  // 该 task_type 映射的 slug 已移出注册表、不再产生新任务，仅展示历史任务快照
  SUBSCRIPTION_RENEWAL_REMIND: { type: '续费提醒（已停用）', category: '功能' },
  SUBSCRIPTION_RENEWAL_SUCCESS: { type: '续费成功', category: '功能' },
  SUPPORT_REPLY: { type: '客服回复', category: '功能' },
};

/** 兜底元数据：DTO 出现未知 task_type 时（理论不应发生）不崩，按功能类透传原始枚举值。 */
function metaOf(taskType: PushTaskType): { type: string; category: '营销' | '功能' } {
  return TASK_TYPE_META[taskType] ?? { type: taskType, category: '功能' };
}

/** 任务状态：后端 EN 枚举 → 页面「状态」筛选与表格展示用的中文。 */
const STATUS_ZH: Record<PushTaskResponse['status'], string> = {
  PENDING: '待发送',
  SENDING: '进行中',
  COMPLETED: '已完成',
  FAILED: '失败',
  CANCELLED: '已取消',
};

/**
 * adaptPushTemplate — PushTemplateResponse → mock PushTemplate（#104：template_slug + category enum）。
 *  type     ← template_slug 中文 label（formatPushTemplateName）
 *  category ← dto.category 派生（OFFER=营销 / 其余功能类=功能；页面「类别」筛选只认 营销/功能 二分）
 *  displayTitle/En ← title_i18n.zh/.en；langs ← title_i18n key 集
 */
export function adaptPushTemplate(dto: PushTemplateResponse): PushTemplate {
  return {
    id: dto.id,
    type: formatPushTemplateName(dto.template_slug),
    category: dto.category === 'OFFER' ? '营销' : '功能',
    // title_i18n 契约 refine 保证 zh/en 必有，但 noUncheckedIndexedAccess 下仍需 ?? 兜底
    displayTitle: dto.title_i18n.zh ?? '',
    originalTitle: dto.title_i18n.en ?? '',
    langs: Object.keys(dto.title_i18n),
    // 接真：upsertTemplate 按 slug 定位 + 编辑保存回传全量 i18n / 启停（见 TemplateDetail 保存）。
    slug: dto.template_slug,
    isActive: dto.is_active,
    isSeedBaseline: dto.is_seed_baseline,
    titleI18n: dto.title_i18n,
    bodyI18n: dto.body_i18n,
    readOnly: dto.content_source === 'CONFIRMED_MESSAGE',
  };
}

export function adaptPushTemplates(dtos: PushTemplateResponse[]): PushTemplate[] {
  return dtos.map(adaptPushTemplate);
}

/**
 * adaptPushTask — PushTaskResponse → mock PushHistoryRow。
 *
 * @param appNameById  app_id → App 名映射（N+1：调用方先取 apps 列表构造，用于把 target_app_id
 *                     还原成 mock 的中文 scope 名）。缺失/未传时回退用 app_id 字符串。
 */
export function adaptPushTask(
  dto: PushTaskResponse,
  appNameById?: Record<string, string>,
): PushHistoryRow {
  const meta = metaOf(dto.task_type);
  const delivered = dto.total_delivered;
  // 缺：真 API 无「总受众」字段。用 已送达 + 失败 近似总目标数（页面仅作展示，非精算口径）。
  const audience = dto.total_delivered + dto.total_failed;
  const scope =
    dto.target_scope === 'all_apps'
      ? '全部 App'
      : ((dto.target_app_id != null ? appNameById?.[dto.target_app_id] : undefined) ??
        dto.target_app_id ??
        '');
  return {
    id: dto.id,
    taskType: dto.task_type,
    type: meta.type,
    category: meta.category,
    scope,
    audience,
    delivered,
    // 缺：真 API 无「打开数」（opened）埋点字段 → null（页面渲染「--」/「未接入」，
    //   不置 0 假装"真任务 0 开启"，也不让页面用它算出假开启率 / NaN）。接入打开率统计端点后再补。
    opened: null,
    // sent_at 为 null（未发送/排期中）时回退 created_at，保证列表时间列不空。
    sentAt: dto.sent_at ?? dto.created_at,
    status: STATUS_ZH[dto.status],
    // operator 接真：dto.operator = created_by 经 admin_users.nickname 解析（ADM-PUSH-S005）。
    // 解析不到（操作员账号已硬删，罕见）→ null，回退空串保持列表不显 "null"。
    operator: dto.operator ?? '',
    // created_by：操作员 admin_users.id，供「操作员」服务端筛选下推（按 created_by 而非 nickname）。
    createdBy: dto.created_by,
  };
}

export function adaptPushTasks(
  dtos: PushTaskResponse[],
  appNameById?: Record<string, string>,
): PushHistoryRow[] {
  return dtos.map((dto) => adaptPushTask(dto, appNameById));
}
